import { stegaClean } from 'next-sanity';

import type {
	BotWizardApiField,
	BotWizardApiMappedNameField,
	BotWizardApiNameField,
	BotWizardApiPersonalityField,
	BotWizardApiResponseSettings,
	BotWizardApiSimpleField,
} from '~services/api/generation/types';
import type {
	BotWizardField as BotWizardFieldSanity,
	BotWizardPreviewContentDependencyType,
	BotWizardPreviewPresetStepType,
	Translated,
} from '~shared/api/sanity';

import {
	isApiFieldName,
	isApiFieldPersonality,
	isApiFieldSimple,
	isConfigFieldButton,
	isConfigFieldColor,
	isConfigFieldImage,
	isConfigFieldSliders,
	isConfigFieldText,
} from './guards';
import type {
	BotWizardConfig,
	BotWizardContentDependencyData,
	BotWizardContentDependencyStep,
	BotWizardField,
	BotWizardFieldDataButton,
	BotWizardFieldDataButtonItem,
	BotWizardFieldDataColor,
	BotWizardFieldDataColorItem,
	BotWizardFieldDataImage,
	BotWizardFieldDataImageItem,
	BotWizardFieldDataSlider,
	BotWizardFieldDataSliderPreset,
	BotWizardFieldDataSlidersItem,
	BotWizardFieldDataText,
	BotWizardFieldDataTextItem,
	BotWizardFieldNames,
	BotWizardPreviewPreset,
	BotWizardPreviewPresetDataItem,
	BotWizardStep,
	TBotWizard,
} from './types';

function createNestedObject<T>(
	data: { path: string[]; element: T }[]
): BotWizardContentDependencyData<T[]> {
	return data.reduce((acc, { path, element }) => {
		let current = acc as Record<string, BotWizardContentDependencyData<T[]>>;

		// Проходим по всем элементам пути кроме последнего
		for (let i = 0; i < path.length - 1; i++) {
			const key = path[i];
			if (!current[key] || typeof current[key] !== 'object') {
				current[key] = {};
			}
			current = current[key] as Record<
				string,
				BotWizardContentDependencyData<T[]>
			>;
		}

		const lastKey = path[path.length - 1];

		if (!current[lastKey]) {
			current[lastKey] = [] as T[];
		} else if (!Array.isArray(current[lastKey])) {
			current[lastKey] = [] as T[];
		}

		current[lastKey].push(element);

		return acc;
	}, {} as BotWizardContentDependencyData<T[]>);
}

export const mapper = (
	config: BotWizardConfig,
	settings: BotWizardApiResponseSettings,
	previewStepBlocks: unknown,
	fields: unknown
): TBotWizard => {
	// TODO: validate types
	const previewStepBlocksTyped = previewStepBlocks as Array<
		{
			_key: string;
			content: Translated<BotWizardPreviewPresetStepType>[];
		} & Omit<BotWizardPreviewContentDependencyType, 'content'>
	>;

	const fieldsTyped = fields as BotWizardFieldSanity[];

	const processField = (
		id: BotWizardFieldNames,
		field:
			| BotWizardApiField<BotWizardApiNameField>
			| BotWizardApiField<BotWizardApiSimpleField>
			| BotWizardApiPersonalityField
	): BotWizardField | null => {
		const fieldFromConfig = config.fields[id];
		if (!fieldFromConfig) {
			return null;
		}

		const createContentDependency = <
			T extends
				| BotWizardFieldDataImageItem
				| BotWizardFieldDataColorItem
				| BotWizardFieldDataButtonItem
				| BotWizardFieldDataSliderPreset
				| BotWizardFieldDataTextItem,
			F extends
				| BotWizardApiMappedNameField
				| BotWizardApiSimpleField
				| BotWizardApiPersonalityField
		>(
			localField: BotWizardApiField<F>,
			map: (item: F) => T
		): BotWizardContentDependencyData<T[]> => {
			if (!localField.dependency || localField.dependency.length === 0) {
				return localField.data.map(map);
			} else {
				const arrayData: { path: string[]; element: T }[] = [];
				localField.data.forEach((el) => {
					if (el.dependency) {
						arrayData.push({
							path: Object.values(el.dependency),
							element: map(el),
						});
					}
				});
				return createNestedObject(arrayData);
			}
		};

		const processFieldData = ():
			| BotWizardFieldDataSlider
			| BotWizardContentDependencyData<BotWizardFieldDataImage>
			| BotWizardContentDependencyData<BotWizardFieldDataColor>
			| BotWizardContentDependencyData<BotWizardFieldDataButton>
			| BotWizardContentDependencyData<BotWizardFieldDataText>
			| null => {
			if (isApiFieldSimple(field, fieldFromConfig.type)) {
				switch (true) {
					case isConfigFieldImage(fieldFromConfig):
						return createContentDependency(
							field,
							(item: BotWizardApiSimpleField): BotWizardFieldDataImageItem => ({
								id: item.id,
								default: item.default,
								text: item.text,
								image: item.path,
								media: item.media,
								example: item.example,
							})
						);
					case isConfigFieldColor(fieldFromConfig):
						return createContentDependency(
							field,
							(item): BotWizardFieldDataColorItem => ({
								id: item.id,
								default: item.default,
								value:
									fieldFromConfig.data.find((el) => el.id === item.id)?.value ||
									'#000',
							})
						);
					case isConfigFieldButton(fieldFromConfig):
						return createContentDependency(
							field,
							(item): BotWizardFieldDataButtonItem => ({
								id: item.id,
								default: item.default,
								text: item.text,
							})
						);
				}
			} else if (
				isApiFieldName(field, fieldFromConfig.type) &&
				isConfigFieldText(fieldFromConfig)
			) {
				return createContentDependency(
					{
						...field,
						data: field.data.reduce((acc, el) => {
							acc.push(
								...el.values.map((e) => ({
									value: e.value,
									dependency: el.dependency,
								}))
							);
							return acc;
						}, [] as BotWizardApiMappedNameField[]),
					},
					(item): BotWizardFieldDataTextItem => item.value
				);
			} else if (
				isApiFieldPersonality(field, fieldFromConfig.type) &&
				isConfigFieldSliders(fieldFromConfig)
			) {
				const sliders = field.data.map(
					(el): BotWizardFieldDataSlidersItem => ({
						id: el.id,
						min: el.values[0].value,
						max: el.values[1].value,
						queryKey: el.id,
					})
				);
				const newPresets = createContentDependency(
					{
						dependency: field.dependency as BotWizardFieldNames[],
						data: field.presetsNew,
					},
					(el) => ({
						id: el.id,
						image: el.path,
						media: el.media,
						name: el.text,
						description: el.comment,
						default: el.default,
						custom: el.id === 'custom',
						values: sliders.map(
							(slider) => el.values.find((e) => e.id === slider.id)?.value || 0
						),
					})
				);

				return {
					isData: true,
					sliders,
					presets: newPresets,
				} as BotWizardFieldDataSlider;
			}

			return null;
		};

		if (fieldFromConfig) {
			const data = processFieldData();
			if (data) {
				return {
					...fieldFromConfig,
					title: fieldsTyped.find((e) => e.id === id)?.title || '',
					contentDependency: field.dependency as BotWizardFieldNames[],
					data,
				} as BotWizardField;
			}
		}

		return null;
	};

	const newFields = Object.fromEntries(
		Object.entries(settings.data)
			.map(([key, value]) => [
				key,
				processField(key as BotWizardFieldNames, value),
			])
			.filter(([_, value]) => !!value)
	) as Record<BotWizardFieldNames, BotWizardField>;

	const processFirstStep = (): BotWizardStep<BotWizardFieldNames[]> => ({
		data: settings.firstStep[0],
		annalName: config.annals.firstStep,
	});

	const processPresetStep = (
		step: BotWizardFieldNames[],
		index: number
	): BotWizardStep<BotWizardContentDependencyStep> => {
		if (
			step.includes('butt') ||
			step.includes('penisSize') ||
			step.includes('boobs')
		) {
			return {
				annalName: config.annals.presetSteps[index],
				data: {
					male: step.filter((el) => el !== 'boobs' && el !== 'butt'),
					female: step.filter((el) => el !== 'penisSize'),
				},
			};
		}
		return {
			annalName: config.annals.presetSteps[index],
			data: step,
		};
	};

	const processPreviewPresetStep = (): BotWizardPreviewPreset => {
		return {
			contentDependency: ['gender'],
			data: Object.fromEntries(
				previewStepBlocksTyped.map(
					(e): [string, BotWizardPreviewPresetDataItem[]] => [
						stegaClean(e.gender)!,
						e.content.map((item): BotWizardPreviewPresetDataItem => {
							return {
								title: item.title,
								text: item.text || '',
								hidden: item.hidden as BotWizardFieldNames,
								image: item.image as BotWizardFieldNames,
							};
						}),
					]
				)
			),
		};
	};

	return {
		firstStep: processFirstStep(),
		presetSteps: settings.presetSteps.map(processPresetStep),
		previewPresetStep: processPreviewPresetStep(),
		fields: newFields,
	};
};
